import { Link } from 'react-router-dom';
import { FileText, Building2, PlusCircle, ClipboardList, BarChart3, ArrowRight } from 'lucide-react';

export default function HomePage() {
  const features = [
    {
      icon: FileText,
      desc: '上传和管理您的合同模板，支持分类标签管理',
      link: '/templates',
      color: 'bg-blue-50 text-blue-600',
    },
    {
      icon: Building2,
      title: '公司信息',
      desc: '维护我方公司主体信息库，支持批量导入',
      link: '/companies',
      color: 'bg-green-50 text-green-600',
    },
    {
      icon: ClipboardList,
      title: '核心条款',
      desc: '填写合同核心条款，支持分期付款和采购明细',
      link: '/clauses',
      color: 'bg-orange-50 text-orange-600',
    },
    {
      icon: PlusCircle,
      title: '生成合同',
      desc: 'AI 智能生成并法审，一键下载可用合同',
      link: '/generate',
      color: 'bg-purple-50 text-purple-600',
    },
    {
      icon: BarChart3,
      title: '合同报表',
      desc: '查看合同生成记录，支持筛选导出',
      link: '/records',
      color: 'bg-teal-50 text-teal-600',
    },
  ];

  return (
    <div className="space-y-8">
      {/* 欢迎横幅 */}
      <div className="bg-gradient-to-r from-primary to-primary-light rounded-2xl p-8 text-white">
        <h1 className="text-2xl font-bold mb-2">欢迎使用合同生成工具 V2</h1>
        <p className="text-white/80 mb-4 max-w-2xl">
          上传合同模板，填写公司信息和核心条款，AI 自动生成规范合同并完成法审，
          生成的合同保留模板全部条款，可直接下载使用。
        </p>
        <div className="flex items-center gap-2 text-sm bg-white/20 w-fit px-3 py-1.5 rounded-full">
          <span>AI 生成 + 法审一体化 · 合同可直接使用</span>
        </div>
      </div>

      {/* 功能卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {features.map((feature) => {
          const Icon = feature.icon;
          return (
            <Link
              key={feature.link}
              to={feature.link}
              className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow group"
            >
              <div className={`w-12 h-12 rounded-lg ${feature.color} flex items-center justify-center mb-4`}>
                <Icon className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-gray-800 mb-2">{feature.title}</h3>
              <p className="text-sm text-gray-500 mb-3">{feature.desc}</p>
              <div className="flex items-center text-sm text-primary font-medium group-hover:gap-2 transition-all">
                开始使用 <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          );
        })}
      </div>

      {/* 使用流程 */}
      <div className="bg-white rounded-xl p-6 border border-gray-100">
        <h2 className="text-lg font-bold text-gray-800 mb-6">使用流程</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { step: '1', title: '上传模板', desc: '上传 Word 合同模板，系统自动识别条款结构' },
            { step: '2', title: '填写信息', desc: '选择公司信息，填写核心条款和采购明细' },
            { step: '3', title: 'AI 生成', desc: 'AI 自动填充信息并保留全部模板条款' },
            { step: '4', title: '下载使用', desc: 'AI 完成法审，下载可直接使用的合同' },
          ].map((item) => (
            <div key={item.step} className="relative p-4 bg-gray-50 rounded-lg">
              <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold text-sm mb-3">
                {item.step}
              </div>
              <h3 className="font-bold text-gray-800 mb-1">{item.title}</h3>
              <p className="text-sm text-gray-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 安全提示 */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-start gap-3">
        <div className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0">i</div>
        <div>
          <h3 className="font-bold text-blue-800 mb-1">数据安全说明</h3>
          <p className="text-sm text-blue-700">
            AI 生成功能通过后端 API 调用大模型，合同模板和填写信息会发送至 AI 平台进行处理。
            我们不会存储您的合同数据，AI 处理完成后数据即被丢弃。
            敏感合同请谨慎使用。
          </p>
        </div>
      </div>
    </div>
  );
}
